import { Component, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { BPProfilesService } from '../../bp-profiles.service';
import { Override } from '../../../../shared/app-decorators';
import { Subscription } from 'rxjs';
import { BPProfilesAttributesService, IProfileAttribute, IProfileAttributeResponse, IProfileAttributeViewModel } from '../../profile-attributes/bp-profiles-attributes.service';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { BPProfilesCaveatsDataService } from './bp-profiles-caveats-data.service';
import { util } from '../../../../shared/util';
import { BPProfilesAttributesComponent } from '../../profile-attributes/bp-profiles-attributes.component';

@Component({
  selector: 'bp-profiles-caveats',
  template: `
    <div class="d-block">
      <faq-popup faqCode="BP_CAVEATS_SUBMISSION"><i class="fa fa-info-circle"></i> <span translate class="margin-left-sm">BPPROFILES.CAVEATS.FAQ_CAVEATS_TITLE</span></faq-popup>

      <h2 *ngIf="loading">
        <activity-indicator></activity-indicator>
      </h2>

      <div class="d-block margin-top-sm" *ngIf="!loading">
        <div class="caveats-group" *ngFor="let group of model | keyvalue">
          <h4 class="caveats-title">
            <text [key]="group.key"></text>
          </h4>
          <bp-profiles-attributes 
            [(model)]="group.value" 
            [editingMode]="editingMode" 
            [showDocumentButton]="true" 
            [getUploadUrl]="getUploadDocumentUrl" 
            [getDocumentUrl]="getDocumentUrl"></bp-profiles-attributes>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./bp-profiiles-caveats.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesCaveatsComponent extends BaseBPProfilesDetailComponent {
  @ViewChildren(BPProfilesAttributesComponent) private bpProfilesAttributesComponents:QueryList<BPProfilesAttributesComponent>;
  public editingMode:boolean;
  public model:IProfileAttributeViewModel;

  private backupCaveatsData:IProfileAttributeResponse;
  private onValidationDeferred:IGADeferred<void>;
  private onValidationSuccessSubscription:Subscription;
  private onValidationErrorSubscription:Subscription;

  constructor(
    private bpProfilesCaveatsDataService:BPProfilesCaveatsDataService,
    private deferredService:GaDeferredService,
    private bpProfilesAttributesService:BPProfilesAttributesService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.editingMode = this.bpProfilesService.editingMode;
    this.loading = true;
    this.bpProfilesCaveatsDataService.getCaveats(this.bpProfilesService.activeProfile?.id)
      .then(this.onCaveatsDataLoaded)
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  @Override(BaseBPProfilesDetailComponent)
  public listen():void {
    super.listen();
    this.onValidationSuccessSubscription = this.bpProfilesAttributesService.onValidationSuccess.subscribe(this.onValidationSuccess);
    this.onValidationErrorSubscription = this.bpProfilesAttributesService.onValidationError.subscribe(this.onValidationError);
  }

  @Override(BaseBPProfilesDetailComponent)
  public onEditChange(isEdit:boolean):void {
    this.editingMode = isEdit;

    if (!isEdit) {
      this.resetModel();
    }
  }

  @Override(BaseBPProfilesDetailComponent)
  public resetModel():void {
    this.model = this.bpProfilesAttributesService.processCaveatsModel(util.copyAndExtend(this.backupCaveatsData.profileAttributes));
  }

  public getUploadDocumentUrl = (caveatId:number):string => {
    return this.bpProfilesCaveatsDataService.getUploadDocumentUrl(this.bpProfilesService.activeProfile?.id, caveatId);
  };

  public getDocumentUrl = (caveatId:number, documentId:number):string => {
    return this.bpProfilesCaveatsDataService.getDocumentUrl(this.bpProfilesService.activeProfile?.id, caveatId, documentId);
  };

  private onCaveatsDataLoaded = (data:IProfileAttributeResponse):void => {
    this.backupCaveatsData = util.copyAndExtend(data);
    this.model = this.bpProfilesAttributesService.processCaveatsModel(data.profileAttributes);
  };

  protected onSave = ():void => {
    // Saving scenario:
    // 1) Validate profile attributes control, notify profile attributes component to start validating fields control
    // 2) Save caveats data because caveats needs to be stored in db before doing add/update/delete on each attributes documents
    // 3) Check profile attribute documents if it needs update or delete synchronously
    // 4) Start upload new documents if any synchronously
    this.onValidationDeferred = this.deferredService.defer();
    this.bpProfilesAttributesService.emitValidationStart();

    const flatModel:IProfileAttribute[] = this.bpProfilesAttributesService.flattenModel(this.model);

    this.setSaveBusy(true);
    this.onValidationDeferred.promise
      .then(() => this.bpProfilesCaveatsDataService.saveCaveats(this.bpProfilesService.activeProfile?.id, this.bpProfilesAttributesService.createProfileAttributesRequestData(flatModel, this.backupCaveatsData.profileAttributes)))
      .then(this.performUpdateAndDeleteDocuments)
      .then(this.performUploadNewDocuments)
      .then(() => {
        return this.bpProfilesCaveatsDataService.getCaveats(this.bpProfilesService.activeProfile?.id)
          .then(this.onCaveatsDataLoaded);
      })
      .catch(this.emitErrorSaving)
      .finally(() => {
        this.setSaveBusy(false);
      });
  };

  private performUpdateAndDeleteDocuments = async():Promise<void> => {
    // we use async and await here to execute promise sequentially
    // rest api supports request one by one otherwise will causing api resource lock
    for (const profileAttribute of this.bpProfilesAttributesService.flattenModel(this.model)) {
      for (const document of profileAttribute.attributeValue?.documents || []) {
        if (document.id && document.isDeleted) {
          await this.bpProfilesCaveatsDataService.deleteCaveatDocument(this.bpProfilesService.activeProfile?.id, profileAttribute.id, document);
        } else if (document.id && document.isUpdated) {
          await this.bpProfilesCaveatsDataService.updateCaveatDocument(this.bpProfilesService.activeProfile?.id, profileAttribute.id, document);
        }
      }
    }
  };

  private performUploadNewDocuments = async():Promise<void> => {
    // we use async and await here to execute file upload promise sequentially
    // rest api supports request one by one otherwise will causing api resource lock
    for (const component of this.bpProfilesAttributesComponents) {
      await component.startUpload();
    }
  };

  private onValidationSuccess = ():void => {
    this.onValidationDeferred.resolve();
  };

  private onValidationError = ():void => {
    this.onValidationDeferred.reject();
  };

  @Override(BaseBPProfilesDetailComponent)
  public ngOnDestroy():void {
    super.ngOnDestroy();
    this.onValidationSuccessSubscription?.unsubscribe();
    this.onValidationErrorSubscription?.unsubscribe();
  }
}